import * as React from 'react';
import styled, { css } from 'styled-components';

import { composeRefs } from '../../internals/composeRefs';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { useClickOutside } from '../../internals/useClickOutside';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useId } from '../../internals/useId';
import { Checkbox } from '../../primitives/Checkbox';
import { Spinner } from '../../primitives/Spinner';
import { Typography } from '../../primitives/Typography';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';
import type { SelectOption } from '../Select';

export type CheckboxSelectMultiVariant = 'checkbox';

export interface CheckboxSelectProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  semanticId?: string;
  name?: string;
  emptyMessage?: string;
}

const transientProps = ['$open', '$error', '$disabled'] as const;

const shouldForwardTransient = (prop: string | number): boolean =>
  !transientProps.includes(prop as (typeof transientProps)[number]);

const StyledRoot = styled.div`
  position: relative;
  width: 100%;
`;

const StyledTrigger = styled.button.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $open: boolean; $error: boolean; $disabled: boolean }>`
  width: 100%;
  height: ${rem(40)};
  min-height: ${rem(40)};
  border-radius: var(--ui-radius-md, ${rem(6)});
  border: ${rem(1)} solid var(--ui-input-border, var(--ui-border, ${palette.LightGray}));
  padding: 0 ${rem(12)};
  background: var(--ui-surface, ${palette.White});
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  /* Match TextInput / Select control elevation */
  filter: drop-shadow(${rem(0)} ${rem(2)} ${rem(4)} var(--ui-input-shadow, var(--ui-shadow-color)));
  transition: border-color 0.2s linear;
  box-sizing: border-box;
  cursor: pointer;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  text-align: left;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: var(--ui-input-border-focus, var(--ui-interactive, ${palette.Blue}));
    outline: none;
  }

  ${({ $error }) =>
    $error &&
    css`
      &:not(:disabled) {
        border-color: var(--ui-status-danger, ${palette.Red});
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      background: var(--ui-background-muted);
      color: var(--ui-text-subtle);
      pointer-events: none;
      opacity: 1;
      filter: none;
    `}

  ${({ $open }) =>
    $open &&
    css`
      border-color: var(--ui-input-border-focus, var(--ui-interactive, ${palette.Blue}));
    `}
`;

const StyledTriggerLabel = styled.span.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $placeholder: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${rem(15)};
  line-height: ${rem(16)};
  color: ${({ $placeholder }) =>
    $placeholder
      ? `var(--ui-text-subtle, ${palette.SpanishGray})`
      : `var(--ui-text, ${palette.Black})`};
`;

const StyledChevron = styled.span`
  flex-shrink: 0;
  font-size: ${rem(20)};
  line-height: 1;
  color: var(--ui-text-muted, ${palette.Gray});
  font-variation-settings: 'FILL' 1;
`;

const StyledPanel = styled.div`
  position: absolute;
  top: calc(100% + ${rem(8)});
  left: 0;
  z-index: ${zIndex.dropdown};
  min-width: 100%;
  width: max-content;
  max-width: ${rem(450)};
  max-height: ${rem(360)};
  padding: ${rem(12)} ${rem(14)};
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  box-shadow: var(--ui-shadow-lg, 0 ${rem(2)} ${rem(20)} rgba(22, 22, 22, 0.12));
  border-radius: var(--ui-radius-lg, ${rem(11)});
  box-sizing: border-box;
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  overflow-y: auto;
  max-height: ${rem(320)};
  min-height: ${rem(80)};
  padding-right: ${rem(4)};

  &::-webkit-scrollbar {
    width: ${rem(5)};
  }

  &::-webkit-scrollbar-thumb {
    background: var(--ui-border-strong, ${palette.SpanishGray});
    border-radius: ${rem(7)};
  }
`;

const StyledOptionRow = styled.div.withConfig({
  shouldForwardProp: shouldForwardTransient,
})<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${rem(10)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: var(--ui-text, inherit);
  opacity: 1;
`;

const StyledEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(120)};
  padding: ${rem(16)};
`;

const StyledLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(120)};
`;

export const CheckboxSelect = React.forwardRef<HTMLDivElement, CheckboxSelectProps>(
  function CheckboxSelect(
    {
      options,
      value,
      onChange,
      onBlur,
      placeholder = 'Select',
      disabled = false,
      loading = false,
      error = false,
      semanticId,
      name,
      emptyMessage = 'No options',
      className,
      style,
      ...rest
    },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const listboxId = useId('checkbox-select-listbox');
    const triggerId = useId('checkbox-select-trigger');
    const semanticProps = getSemanticAttributes(semanticId);
    const selectedSet = React.useMemo(() => new Set(value), [value]);

    const selectedLabel = React.useMemo(() => {
      if (value.length === 0) return null;
      return options
        .filter((opt) => selectedSet.has(opt.value))
        .map((opt) => opt.label)
        .join(', ');
    }, [options, selectedSet, value.length]);

    const close = React.useCallback(() => {
      setOpen(false);
    }, []);

    useClickOutside(rootRef, close, open);
    useEscapeKey(() => {
      close();
      triggerRef.current?.focus();
    }, open);

    const toggleOpen = React.useCallback(() => {
      if (disabled) return;
      setOpen((prev) => !prev);
    }, [disabled]);

    const toggleValue = React.useCallback(
      (optionValue: string, optionDisabled?: boolean) => {
        if (disabled || optionDisabled) return;
        const next = selectedSet.has(optionValue)
          ? value.filter((v) => v !== optionValue)
          : [...value, optionValue];
        onChange(next);
      },
      [disabled, onChange, selectedSet, value],
    );

    const composedRootRef = React.useMemo(() => composeRefs(ref, rootRef), [ref]);

    return (
      <StyledRoot
        ref={composedRootRef}
        className={className}
        style={style}
        data-state={open ? 'open' : 'closed'}
        {...semanticProps}
        {...rest}
      >
        <StyledTrigger
          ref={triggerRef}
          type='button'
          id={semanticId ?? triggerId}
          name={name}
          $open={open}
          $error={error}
          $disabled={disabled}
          disabled={disabled}
          aria-haspopup='listbox'
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-invalid={error || undefined}
          onClick={toggleOpen}
          onBlur={onBlur}
        >
          <StyledTriggerLabel $placeholder={!selectedLabel}>
            {selectedLabel ?? placeholder}
          </StyledTriggerLabel>
          <StyledChevron className='material-symbols-rounded' aria-hidden>
            arrow_drop_down
          </StyledChevron>
        </StyledTrigger>

        {open && (
          <StyledPanel
            id={listboxId}
            role='listbox'
            aria-multiselectable
            aria-labelledby={semanticId ?? triggerId}
          >
            {loading ? (
              <StyledLoading>
                <Spinner size='sm' />
              </StyledLoading>
            ) : options.length === 0 ? (
              <StyledEmpty>
                <Typography variant='body-s' color='var(--ui-text-muted)'>
                  {emptyMessage}
                </Typography>
              </StyledEmpty>
            ) : (
              <StyledList>
                {options.map((option) => {
                  const checked = selectedSet.has(option.value);
                  const optionDisabled = Boolean(option.isDisabled);
                  return (
                    <StyledOptionRow
                      key={option.value}
                      $disabled={optionDisabled}
                      role='option'
                      aria-selected={checked}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={disabled || optionDisabled}
                        label={option.label}
                        semanticId={`${semanticId ?? name ?? 'checkboxSelect'}.${option.value}`}
                        onChange={() => toggleValue(option.value, option.isDisabled)}
                      />
                    </StyledOptionRow>
                  );
                })}
              </StyledList>
            )}
          </StyledPanel>
        )}
      </StyledRoot>
    );
  },
);

CheckboxSelect.displayName = 'CheckboxSelect';
